/**
 * Notification Manager
 * 
 * Utility to batch and manage notifications to prevent notification spam
 */

// Store for pending notifications
const notificationQueue = [];
let isProcessing = false;
let batchTimeout = null;
const BATCH_DELAY = 2000; // 2 seconds

/**
 * Add a notification to the queue
 * 
 * @param {Object} notification - The notification object
 * @param {string} notification.message - The notification message
 * @param {string} notification.severity - The notification severity (info, success, warning, error)
 * @param {Function} setSnackbar - The setState function to update the snackbar
 */
export const queueNotification = (notification, setSnackbar) => {
  // Check if notification is already in queue
  const isDuplicate = notificationQueue.some(item => 
    item.message === notification.message && 
    item.severity === notification.severity
  );
  
  // Don't add duplicates
  if (isDuplicate) return;
  
  // Add to queue
  notificationQueue.push({
    ...notification,
    setSnackbar,
    timestamp: Date.now()
  });
  
  // Start processing if not already
  if (!isProcessing) {
    processNotificationQueue();
  }
};

/**
 * Process the notification queue
 */
const processNotificationQueue = () => {
  // Clear any existing timeout
  if (batchTimeout) {
    clearTimeout(batchTimeout);
  }
  
  // If queue is empty, stop processing
  if (notificationQueue.length === 0) {
    isProcessing = false;
    return;
  }
  
  isProcessing = true;
  
  // Set timeout to batch notifications
  batchTimeout = setTimeout(() => {
    // Get the most recent notification
    const latestNotification = notificationQueue.pop();
    
    // If we have multiple notifications of the same type, batch them
    const similarNotifications = notificationQueue.filter(
      n => n.severity === latestNotification.severity
    );
    
    // Clear the queue of similar notifications
    while (notificationQueue.findIndex(n => n.severity === latestNotification.severity) !== -1) {
      const index = notificationQueue.findIndex(n => n.severity === latestNotification.severity);
      notificationQueue.splice(index, 1);
    }
    
    // Show a batched notification if needed
    if (similarNotifications.length > 0) {
      latestNotification.setSnackbar({
        open: true,
        message: `${similarNotifications.length + 1} mises à jour ${latestNotification.severity === 'info' ? 'informatives' : 
                  latestNotification.severity === 'success' ? 'réussies' : 
                  latestNotification.severity === 'warning' ? 'd\'avertissement' : 
                  'd\'erreur'}`,
        severity: latestNotification.severity
      });
    } else {
      // Show the single notification
      latestNotification.setSnackbar({
        open: true,
        message: latestNotification.message,
        severity: latestNotification.severity
      });
    }
    
    // Continue processing queue
    processNotificationQueue();
  }, BATCH_DELAY);
};

/**
 * Check if notifications should be shown based on global settings
 */
export const shouldShowNotification = () => {
  if (typeof window.__travelIT === 'undefined') {
    return true;
  }
  
  return !window.__travelIT.silentMode;
};

/**
 * Set the global notification mode
 * 
 * @param {boolean} silent - Whether to suppress notifications
 */
export const setSilentMode = (silent = true) => {
  if (typeof window.__travelIT === 'undefined') {
    window.__travelIT = {};
  }
  
  window.__travelIT.silentMode = silent;
};